import { AnalyticsPanel } from '@/shared/components/ui/AnalyticsPanel';
import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/shared/api/axios";
import { Card, CardBody } from "@/shared/components/ui/Card";
import { Button } from "@/shared/components/ui/Button";
import { Input } from "@/shared/components/ui/Input";
import { Select } from "@/shared/components/ui/Select";
import { Modal } from "@/shared/components/ui/Modal";
import {
  CheckCircle,
  XCircle,
  Receipt,
  Wallet,
  CheckCircle2,
  Clock,
  TrendingUp,
  BarChart3,
  Layers,
  Calendar, Banknote } from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from "recharts";
import { formatINR } from "@/shared/utils/currency";
import { AccountingSubNav } from "@/features/accounting/components/AccountingSubNav";
import { format } from "date-fns";
import toast from "react-hot-toast";
import { RecurringProfilesList } from "../components/RecurringProfilesList";
import { AdvancedExpenseTabs, RecurringV2Panel } from "../components/AdvancedExpenseTabs";
import { ExpenseWorkspaceTab } from "../components/ExpenseWorkspaceTab";
import { ADVANCED_EXPENSE_TABS, type AdvancedExpenseTab } from "../components/expenseTabs";
import { useUser } from "@/shared/hooks/useAuth";
import { useExpensesV2 } from "../hooks/useExpenseApi";

import { PageHeader } from '@/shared/components/ui/PageHeader';
import { StatCard, StatGrid } from '@/shared/components/ui/StatCard';
const DEFAULT_CATEGORIES = [
  "travel",
  "office",
  "marketing",
  "utility",
  "salary",
  "fuel",
  "misc",
  "rent",
  "internet",
  "legal",
  "insurance",
  "repairs",
  "bank_charges",
];
const PAYMENT_MODES = ["cash", "bank_transfer", "upi", "cheque", "card"];
const SETTLEMENT_MODES = [
  { value: "company_paid", label: "Company (Direct Payment)" },
  { value: "employee_paid", label: "Employee (Reimbursable)" },
];

function useExpenses(status?: string) {
  return useQuery({
    queryKey: ["accounts", "expenses", status],
    queryFn: async () => {
      const params = status ? `?status=${status}` : "";
      const { data } = await api.get(`/api/accounts/expenses${params}`);
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.data)) return data.data;
      if (Array.isArray(data?.items)) return data.items;
      return [];
    },
  });
}

export interface ExpensesPageProps {
  /**
   * Which tab to open on mount. Lets /accounting-v2/expenses/register deep-link
   * straight to the financial register while still rendering the one page, so
   * there is no duplicate register screen.
   */
  initialView?: "expenses" | "legacy" | "recurring" | "recurring-v2" | AdvancedExpenseTab;
}

export function ExpensesPage({ initialView }: ExpensesPageProps = {}) {
  const currentUser = useUser();
  const isEmployee = currentUser?.role === 'employee';

  // 'expenses' and 'recurring' are the original legacy views; 'recurring-v2'
  // plus every AdvancedExpenseTab id are the Accounting V2 surfaces.
  const [view, setView] = useState<
    "expenses" | "legacy" | "recurring" | "recurring-v2" | AdvancedExpenseTab
  >(initialView && initialView !== 'approvals' ? initialView : "expenses");

  const effectiveView = isEmployee && view === "approvals" ? "expenses" : view;

  const [filter, setFilter] = useState("");
  const { data: rawExpenses } = useExpenses(filter);
  const expenses = Array.isArray(rawExpenses) ? rawExpenses : [];

  const { data: v2Data } = useExpensesV2({ pageSize: 200 });
  const v2Items = v2Data?.items ?? [];

  const metrics = useMemo(() => {
    let totalGrossMinor = 0;
    let submittedMinor = 0;
    let submittedCount = 0;
    let paidMinor = 0;
    let paidCount = 0;
    let reimbursementDueMinor = 0;
    let reimbursementDueCount = 0;

    v2Items.forEach((e) => {
      if (['approved', 'posted', 'paid', 'partially_paid'].includes(e.status)) {
        totalGrossMinor += e.grossAmountMinor || 0;
      }
      if (e.status === 'submitted') {
        submittedMinor += e.grossAmountMinor || 0;
        submittedCount += 1;
      }
      if (e.status === 'paid' || e.paymentStatus === 'paid') {
        paidMinor += e.grossAmountMinor || 0;
        paidCount += 1;
      }
      if (
        e.settlementMode === 'employee_paid' &&
        e.paymentStatus !== 'paid' &&
        !['rejected', 'cancelled'].includes(e.status)
      ) {
        reimbursementDueMinor += e.grossAmountMinor || 0;
        reimbursementDueCount += 1;
      }
    });

    return {
      totalGross: totalGrossMinor / 100,
      submittedAmount: submittedMinor / 100,
      submittedCount,
      paidAmount: paidMinor / 100,
      paidCount,
      reimbursementDueAmount: reimbursementDueMinor / 100,
      reimbursementDueCount,
      totalCount: v2Items.length,
    };
  }, [v2Items]);

  const { trendData, categoryData } = useMemo(() => {
    // 1. Monthly / Period Trend
    const monthMap: Record<string, { label: string; spend: number; count: number }> = {};
    // 2. Category Distribution
    const catMap: Record<string, { label: string; amount: number; count: number }> = {};

    v2Items.forEach((e) => {
      const gross = (e.grossAmountMinor || 0) / 100;
      const d = e.expenseDate
        ? new Date(e.expenseDate).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })
        : 'Recent';
      if (!monthMap[d]) monthMap[d] = { label: d, spend: 0, count: 0 };
      monthMap[d].spend += gross;
      monthMap[d].count += 1;

      const cat = e.categoryLabel || 'General Operations';
      if (!catMap[cat]) catMap[cat] = { label: cat, amount: 0, count: 0 };
      catMap[cat].amount += gross;
      catMap[cat].count += 1;
    });

    const trend = Object.values(monthMap).slice(-10);
    const categories = Object.values(catMap)
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 6);

    return { trendData: trend, categoryData: categories };
  }, [v2Items]);

  // ── Dynamic Master Data ──────────────────────────────────────────────────────
  const { data: dynamicCategoriesRaw } = useQuery({
    queryKey: ["accounts", "expense-categories"],
    queryFn: async () => {
      try {
        const { data } = await api.get("/api/accounting/expenses/categories");
        const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        return list;
      } catch (_) {
        return [];
      }
    },
  });

  const { data: accountsRaw } = useQuery({
    queryKey: ["accounts", "cash-bank-accounts"],
    queryFn: async () => {
      try {
        const { data } = await api.get("/api/accounting/accounts");
        const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        return list.filter((a: any) =>
          !a.isGroupHeader &&
          a.allowManualPosting !== false &&
          (a.primaryGroup === 'Cash & Bank' ||
           a.subtype === 'cash' ||
           a.subtype === 'bank' ||
           ['1010', '1020', '1021', '1030'].includes(a.code) ||
           (a.code && a.code.startsWith('10') && !['1000', '1100', '1200', '1300', '1310', '1400', '1500', '1600', '1700'].includes(a.code)))
        );
      } catch (_) {
        return [];
      }
    },
  });

  const { data: vendorsRaw } = useQuery({
    queryKey: ["accounts", "vendors-parties"],
    queryFn: async () => {
      try {
        const { data } = await api.get("/api/accounting/parties?role=supplier");
        return Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      } catch (_) {
        return [];
      }
    },
  });

  const categoryOptions = Array.from(
    new Set([
      ...DEFAULT_CATEGORIES,
      ...(dynamicCategoriesRaw || []).map((c: any) => String(c.code || c.name || '').toLowerCase().replace(/\s+/g, '_')),
    ])
  ).filter(Boolean);

  const cashBankAccounts = (accountsRaw && accountsRaw.length > 0)
    ? accountsRaw
    : [{ code: '1220', name: 'Cash in Hand' }, { code: '1222', name: 'Bank – Current Account' }];

  const vendors = vendorsRaw || [];

  const qc = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    category: "office",
    amount: "",
    gstAmount: "",
    vendorName: "",
    description: "",
    paymentMode: "cash",
    paidFromAccount: "1010",
    settlementMode: "company_paid",
    employeeName: "",
    employeeId: "",
  });

  const createMutation = useMutation({
    mutationFn: async (payload: any) => {
      const { data } = editingId
        ? await api.put(`/api/accounts/expenses/${editingId}`, payload)
        : await api.post("/api/accounts/expenses", payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounts", "expenses"] });
      qc.invalidateQueries({ queryKey: ["accounting-expenses"] });
      setIsOpen(false);
      setEditingId(null);
      toast.success(
        editingId
          ? "Expense updated and resubmitted"
          : "Expense submitted for approval",
      );
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/api/accounts/expenses/${id}/approve`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounts", "expenses"] });
      qc.invalidateQueries({ queryKey: ["accounting-expenses"] });
      toast.success("Expense approved & posted to ledger");
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const { data } = await api.post(`/api/accounts/expenses/${id}/reject`, {
        reason,
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounts", "expenses"] });
      qc.invalidateQueries({ queryKey: ["accounting-expenses"] });
      toast.success("Expense rejected");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/accounts/expenses/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounts", "expenses"] });
      qc.invalidateQueries({ queryKey: ["accounting-expenses"] });
      toast.success("Expense deleted");
    },
  });
  const reverseMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post(
        `/api/accounts/expenses/${id}/reverse`,
        { reason },
        { headers: { "X-Idempotency-Key": crypto.randomUUID() } },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounts", "expenses"] });
      qc.invalidateQueries({ queryKey: ["accounting-expenses"] });
      toast.success("Expense reversed");
    },
  });

  const setField = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const handleCreate = async () => {
    if (!form.amount || !form.category) return;
    await createMutation.mutateAsync({
      ...form,
      amount: parseFloat(form.amount),
      gstAmount: parseFloat(form.gstAmount || "0"),
    });
  };

  // openNew() was deleted with the legacy create button. Creating an expense is
  // ExpenseFormModal's job now, so there is exactly one creation flow.
  const openEdit = (expense: any) => {
    setEditingId(expense._id);
    setForm({
      date: new Date(expense.date).toISOString().slice(0, 10),
      category: expense.category,
      amount: String(expense.amount),
      gstAmount: String(expense.gstAmount || ""),
      vendorName: expense.vendorName || "",
      description: expense.description || "",
      paymentMode: expense.paymentMode || "cash",
      paidFromAccount: expense.paidFromAccount || "1010",
      settlementMode: expense.settlementMode || "company_paid",
      employeeName: expense.employeeName || "",
      employeeId: expense.employeeId || "",
    });
    setIsOpen(true);
  };

  const statusBadge = (s: string) => {
    const c: Record<string, string> = {
      pending: "bg-yellow-100 text-yellow-800",
      posted: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      approved: "bg-blue-100 text-blue-800",
    };
    return (
      <span
        className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${c[s] || "bg-gray-100 text-gray-600"}`}
      >
        {s}
      </span>
    );
  };

  return (
    <div className="min-h-screen">
      <AccountingSubNav />

      <div className="mx-auto max-w-[1580px] space-y-5 pb-16">
        <PageHeader
          icon={Receipt}
          eyebrow="Accounts"
          title="Expenses"
          description="Capture operating expenses, route them for approval, reimburse staff and post them to the ledger."
        />

        <StatGrid cols={4}>
          <StatCard label="Total Spend" value={formatINR(metrics.totalGross)} hint={`${metrics.totalCount} approved & posted ${metrics.totalCount === 1 ? 'entry' : 'entries'}`} icon={Wallet} tone="primary" />
          <StatCard label="Awaiting Approval" value={formatINR(metrics.submittedAmount)} hint={`${metrics.submittedCount} in review`} icon={Clock} tone="warning" />
          <StatCard label="Paid" value={formatINR(metrics.paidAmount)} hint={`${metrics.paidCount} fully paid`} icon={CheckCircle2} tone="success" />
          <StatCard label="Reimbursement Due" value={formatINR(metrics.reimbursementDueAmount)} hint={`${metrics.reimbursementDueCount} claims pending payout`} icon={Banknote} tone="info" />
        </StatGrid>

        {/* Visual Charts Grid */}
        <AnalyticsPanel storageKey="expenses" title="Expense analytics" description="Spend trajectory and top categories">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Chart 1: Spend Trajectory Area Chart */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.22, ease: [0.4, 0, 0.2, 1] }}
            className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-xs"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-6">
              <div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  <h2 className="text-base font-bold text-slate-900">Spend & Burn Rate Trajectory</h2>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  Chronological progression of operating expenditures
                </p>
              </div>
              <div className="flex items-center gap-2 text-xs font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-lg border border-blue-100">
                <span className="w-2.5 h-2.5 rounded-full bg-blue-600 inline-block" />
                Gross Spend (₹)
              </div>
            </div>

            <div className="h-56 w-full">
              {trendData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-xs text-slate-400">
                  No expense records available to plot spend trajectory.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trendData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorExpenseSpend" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2563EB" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#2563EB" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                    <XAxis
                      dataKey="label"
                      tickLine={false}
                      axisLine={{ stroke: '#CBD5E1' }}
                      tick={{ fill: '#64748B', fontSize: 11, fontWeight: 500 }}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={{ stroke: '#CBD5E1' }}
                      tick={{ fill: '#64748B', fontSize: 11 }}
                      tickFormatter={(val) => `₹${val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}`}
                    />
                    <Tooltip
                      formatter={(val: any) => [formatINR(Number(val)), 'Spend']}
                      contentStyle={{
                        backgroundColor: '#FFFFFF',
                        borderColor: '#E2E8F0',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        fontSize: '12px',
                        fontWeight: 600,
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="spend"
                      name="Gross Spend"
                      stroke="#2563EB"
                      strokeWidth={2.5}
                      fillOpacity={1}
                      fill="url(#colorExpenseSpend)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </motion.div>

          {/* Chart 2: Top Categories Bar Chart */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-xs"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-6">
              <div>
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                  <h2 className="text-base font-bold text-slate-900">Top Expenditure Categories</h2>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  Cost concentration across primary operational departments
                </p>
              </div>
              <div className="text-xs font-semibold text-slate-500">
                Ranked by volume
              </div>
            </div>

            <div className="h-56 w-full">
              {categoryData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-xs text-slate-400">
                  No category data available yet.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={categoryData}
                    layout="vertical"
                    margin={{ top: 5, right: 15, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
                    <XAxis
                      type="number"
                      tickLine={false}
                      axisLine={{ stroke: '#CBD5E1' }}
                      tick={{ fill: '#64748B', fontSize: 11 }}
                      tickFormatter={(val) => `₹${val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val}`}
                    />
                    <YAxis
                      dataKey="label"
                      type="category"
                      tickLine={false}
                      axisLine={{ stroke: '#CBD5E1' }}
                      tick={{ fill: '#475569', fontSize: 11, fontWeight: 600 }}
                      width={110}
                    />
                    <Tooltip
                      formatter={(val: any) => [formatINR(Number(val)), 'Total Cost']}
                      contentStyle={{
                        backgroundColor: '#FFFFFF',
                        borderColor: '#E2E8F0',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        fontSize: '12px',
                        fontWeight: 600,
                      }}
                    />
                    <Bar dataKey="amount" radius={[0, 6, 6, 0]} maxBarSize={22}>
                      {categoryData.map((_, index) => {
                        const colors = ['#2563EB', '#7C3AED', '#059669', '#D97706', '#DC2626', '#475569'];
                        return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </motion.div>
        </div>
        </AnalyticsPanel>

        {/* Enhanced Segmented Pill Tabs Navigation */}
        <div className="bg-white rounded-2xl border border-slate-200/80 p-1.5 shadow-xs overflow-x-auto">
          <div className="flex items-center gap-1.5 min-w-max">
            <button
              onClick={() => setView('expenses')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all ${
                effectiveView === 'expenses'
                  ? 'bg-primary text-white shadow-sm '
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              <Receipt className="w-4 h-4" />
              Expenses Workspace
            </button>
            <button
              onClick={() => setView('recurring-v2')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all ${
                effectiveView === 'recurring-v2'
                  ? 'bg-primary text-white shadow-sm '
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              <Calendar className="w-4 h-4" />
              Scheduled Expenses
            </button>
            <button
              onClick={() => setView('recurring')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all ${
                effectiveView === 'recurring'
                  ? 'bg-primary text-white shadow-sm '
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              <Clock className="w-4 h-4" />
              Recurring Profiles
            </button>
            {ADVANCED_EXPENSE_TABS.filter((t) => !isEmployee || t.id !== 'approvals').map((t) => (
              <button
                key={t.id}
                onClick={() => setView(t.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all ${
                  effectiveView === t.id
                    ? 'bg-primary text-white shadow-sm '
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
              >
                {t.label}
              </button>
            ))}
            <button
              onClick={() => setView('legacy')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all ${
                effectiveView === 'legacy'
                  ? 'bg-primary text-white shadow-sm '
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              <Layers className="w-4 h-4" />
              Legacy Archive
            </button>
          </div>
        </div>

        {/* Tab Content Rendering */}
        <div className="mt-4">

      {effectiveView === "recurring" ? (
        <RecurringProfilesList kind="expense" />
      ) : effectiveView === "recurring-v2" ? (
        <RecurringV2Panel />
      ) : effectiveView === "expenses" ? (
        // Operational workspace over acc_expenses — the single source of truth.
        // Shares its form, service and query key with the Expense Register.
        <ExpenseWorkspaceTab />
      ) : effectiveView === "legacy" ? (
        <>
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Read-only archive of expenses captured before the register was unified. These live in the
            legacy <code>expenses</code> collection and are not part of the accounting ledger. Create
            new expenses from the Expenses tab.
          </div>
          <div className="flex gap-2">
            {["", "draft", "pending", "posted", "rejected", "reversed"].map(
              (s) => (
                <button
                  key={s}
                  onClick={() => setFilter(s)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border transition ${filter === s ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border text-muted-foreground hover:text-foreground"}`}
                >
                  {s || "All"}
                </button>
              ),
            )}
          </div>

          <Card className="shadow-xs">
            <CardBody className="p-0">
              <div className="overflow-auto max-h-[500px]">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs uppercase bg-muted/50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3">Exp #</th>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Category</th>
                      <th className="px-4 py-3">Vendor</th>
                      <th className="px-4 py-3 text-right">Amount</th>
                      <th className="px-4 py-3 text-right">GST</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(!expenses || expenses.length === 0) && (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-4 py-8 text-center text-muted-foreground"
                        >
                          No expenses found.
                        </td>
                      </tr>
                    )}
                    {expenses?.map((exp: any) => (
                      <tr key={exp._id} className="hover:bg-muted/20">
                        <td className="px-4 py-3 font-medium">
                          {exp.expenseNumber}
                        </td>
                        <td className="px-4 py-3">
                          {new Date(exp.date).toLocaleDateString("en-IN")}
                        </td>
                        <td className="px-4 py-3 capitalize">{exp.category}</td>
                        <td className="px-4 py-3">{exp.vendorName || "-"}</td>
                        <td className="px-4 py-3 text-right font-medium">
                          {formatINR(exp.amount)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {exp.gstAmount ? `₹${exp.gstAmount}` : "-"}
                        </td>
                        <td className="px-4 py-3">{statusBadge(exp.status)}</td>
                        <td className="px-4 py-3">
                          {exp.status === "pending" && (
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  approveMutation.mutateAsync(exp._id)
                                }
                                className="text-green-600"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const r = prompt("Rejection reason:");
                                  if (r)
                                    rejectMutation.mutateAsync({
                                      id: exp._id,
                                      reason: r,
                                    });
                                }}
                                className="text-red-600"
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                          {["draft", "pending", "rejected"].includes(
                            exp.status,
                          ) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEdit(exp)}
                            >
                              Edit
                            </Button>
                          )}
                          {["draft", "rejected"].includes(exp.status) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                confirm("Delete this expense?") &&
                                deleteMutation.mutate(exp._id)
                              }
                            >
                              Delete
                            </Button>
                          )}
                          {exp.status === "posted" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const reason = prompt("Reversal reason:");
                                if (reason)
                                  reverseMutation.mutate({
                                    id: exp._id,
                                    reason,
                                  });
                              }}
                            >
                              Reverse
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </>
      ) : (
        <AdvancedExpenseTabs tab={effectiveView as AdvancedExpenseTab} />
      )}
      </div>
      </div>

      <Modal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        title={editingId ? "Edit Expense" : "New Expense"}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending ? "Submitting..." : "Submit Expense"}
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-2 gap-4 py-4">
          <div>
            <label className="text-sm font-medium">Date</label>
            <Input
              name="date"
              type="date"
              value={form.date}
              onChange={(e) => setField("date", e.target.value)}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Category</label>
            <Select
              name="category"
              value={form.category}
              onChange={(e) => setField("category", e.target.value)}
              options={categoryOptions.map((c) => ({
                value: c,
                label: c.replace(/_/g, " ").toUpperCase(),
              }))}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Amount (₹)</label>
            <Input
              name="amount"
              type="number"
              value={form.amount}
              onChange={(e) => setField("amount", e.target.value)}
            />
          </div>
          <div>
            <label className="text-sm font-medium">GST Amount</label>
            <Input
              name="gstAmount"
              type="number"
              value={form.gstAmount}
              onChange={(e) => setField("gstAmount", e.target.value)}
              placeholder="0"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Vendor</label>
            <Input
              name="vendorName"
              list="expenses-vendor-datalist"
              placeholder="Select or type vendor name..."
              value={form.vendorName}
              onChange={(e) => setField("vendorName", e.target.value)}
            />
            <datalist id="expenses-vendor-datalist">
              {vendors.map((v: any) => (
                <option key={v._id} value={v.displayName || v.legalName}>
                  {v.contactId ? `${v.contactId} — ` : ''}{v.displayName || v.legalName}
                </option>
              ))}
            </datalist>
          </div>
          <div>
            <label className="text-sm font-medium">Payment Mode</label>
            <Select
              name="paymentMode"
              value={form.paymentMode}
              onChange={(e) => setField("paymentMode", e.target.value)}
              options={PAYMENT_MODES.map((m) => ({
                value: m,
                label: m.replace(/_/g, " ").toUpperCase(),
              }))}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Funded By</label>
            <Select
              name="settlementMode"
              value={form.settlementMode}
              onChange={(e) => setField("settlementMode", e.target.value)}
              options={SETTLEMENT_MODES}
            />
          </div>
          {form.settlementMode !== "employee_paid" && (
            <div>
              <label className="text-sm font-medium">Paid From Account</label>
              <Select
                name="paidFromAccount"
                value={form.paidFromAccount}
                onChange={(e) => setField("paidFromAccount", e.target.value)}
                options={cashBankAccounts.map((a: any) => ({
                  value: a.code,
                  label: `${a.code} — ${a.name}`,
                }))}
              />
            </div>
          )}
          {form.settlementMode === "employee_paid" && (
            <>
              <div>
                <label className="text-sm font-medium">Employee Name <span className="text-red-500">*</span></label>
                <Input
                  name="employeeName"
                  placeholder="e.g. Rahul Sharma"
                  value={form.employeeName}
                  onChange={(e) => setField("employeeName", e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Employee ID</label>
                <Input
                  name="employeeId"
                  placeholder="e.g. EMP-101"
                  value={form.employeeId}
                  onChange={(e) => setField("employeeId", e.target.value)}
                />
              </div>
            </>
          )}
          <div className="col-span-2">
            <label className="text-sm font-medium">Description</label>
            <Input
              name="description"
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              placeholder="What was this expense for?"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
